import React from "react";

/**
 * Minimal, focused ErrorBoundary. Renders a friendly fallback so a broken
 * section (e.g. a runtime error in a banner branch) never blank-screens the page.
 *
 * Usage:
 *   <ErrorBoundary sectionName="Hero">
 *     <HeroSection />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Surface to the console for debugging; never crash the page.
        // eslint-disable-next-line no-console
        console.error(`[ErrorBoundary:${this.props.sectionName || "section"}]`, error, info);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        if (typeof this.props.fallback === "function") {
            return this.props.fallback({ error: this.state.error, retry: this.handleRetry });
        }
        if (this.props.fallback) return this.props.fallback;

        return (
            <div
                data-testid={`error-boundary-${this.props.sectionName || "section"}`}
                className="my-6 mx-auto max-w-3xl rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center"
            >
                <h3 className="font-heading font-semibold text-brand-navy text-lg">
                    Something went wrong loading this section.
                </h3>
                <p className="text-sm text-brand-muted mt-2">
                    The rest of the page still works. You can retry, or head to another page.
                </p>
                <div className="mt-4 flex justify-center gap-3">
                    <button
                        type="button"
                        onClick={this.handleRetry}
                        className="btn-navy text-sm"
                        data-testid="error-boundary-retry"
                    >
                        Try again
                    </button>
                    <a href="/" className="btn-outline text-sm">Go home</a>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
